import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CentroDeCusto,
  FormaPagamento,
  SituacaoLancamento,
  TipoLancamento,
} from '@arena/shared/dominio';
import { CENTRO_DE_CUSTO, SITUACAO_LANCAMENTO, TIPO_LANCAMENTO } from '@arena/shared/dominio';
import {
  ehTipoDeAnexo,
  FORMAS_DO_LANCAMENTO_MANUAL,
  FREQUENCIA_RECORRENCIA,
  ROTULO_CENTRO_DE_CUSTO,
  ROTULO_FORMA_PAGAMENTO,
  ROTULO_FREQUENCIA,
  ROTULO_TIPO_LANCAMENTO,
  zNovoLancamento,
  type FrequenciaRecorrencia,
} from '@arena/shared/schemas';
import { paraCentavos } from '../agenda/formatos.js';
import { comBase, ErroDeApi, mensagemDoErro } from '../api/cliente.js';
import { financeiroApi } from '../api/financeiro.js';
import { Icone } from '../ui/Icone.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Escolhas, Formulario } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';

/**
 * `#m-lancamento` — novo lançamento do razão.
 *
 * O formulário valida com `zNovoLancamento`, o MESMO schema que o
 * servidor usa no corpo da requisição — igual `ModalDeReserva.tsx` faz
 * com `zNovaMarcacao`.
 *
 * Ficou de fora, de propósito, o detalhamento da folha (campo `folha`
 * do schema): nem o protótipo mostra esse campo no formulário, e o
 * modelo de remuneração do professor é uma decisão em aberto do
 * documento 11. Categoria "Professores" lança o VALOR TOTAL, sem
 * repartir — o rateio, quando existir, entra ao REALIZAR o pagamento,
 * não aqui.
 *
 * O anexo segue o fluxo do documento 11: o arquivo vai direto do
 * navegador para o Storage, nunca pelo backend Node. `comBase()`
 * resolve a URL de envio para o mesmo prefixo que `api()` usa — ver o
 * comentário em `api/cliente.ts`.
 */

export function ModalDeLancamento({ hoje, onFechar }: { hoje: string; onFechar: () => void }) {
  const avisar = useToast();
  const cliente = useQueryClient();

  const categoriasQuery = useQuery({
    queryKey: ['financeiro', 'categorias'],
    queryFn: financeiroApi.categorias,
  });

  const [tipo, setTipo] = useState<TipoLancamento>('Saida');
  const [categoriaId, setCategoriaId] = useState('');
  const [subcategoria, setSubcategoria] = useState('');
  const [valor, setValor] = useState('');
  const [data, setData] = useState(hoje);
  const [centroDeCusto, setCentroDeCusto] = useState<CentroDeCusto>(CENTRO_DE_CUSTO[0]);
  const [forma, setForma] = useState<FormaPagamento>(FORMAS_DO_LANCAMENTO_MANUAL[0]);
  const [situacao, setSituacao] = useState<SituacaoLancamento>(SITUACAO_LANCAMENTO[0]);
  const [recorrencia, setRecorrencia] = useState<FrequenciaRecorrencia | ''>('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);

  // Só as categorias do TIPO escolhido — filtrado no cliente, como o
  // briefing pede. Se a categoria guardada não é mais uma delas (o
  // tipo mudou, ou as categorias ainda estão carregando), a primeira
  // da lista vira a efetiva — sem useEffect, é só uma derivação.
  const categoriasDoTipo = (categoriasQuery.data ?? []).filter((c) => c.tipo === tipo);
  const categoriaEfetiva = categoriasDoTipo.some((c) => c.id === categoriaId)
    ? categoriaId
    : (categoriasDoTipo[0]?.id ?? '');

  const criar = useMutation({
    mutationFn: financeiroApi.criar,
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['financeiro'] }),
  });

  /**
   * O anexo só é enviado DEPOIS do lançamento existir. Falha aqui vira
   * aviso, nunca desfaz a criação — a pessoa tenta anexar de novo
   * depois.
   */
  async function enviarAnexoSeHouver(lancamentoId: string): Promise<void> {
    if (!arquivo) return;
    // `accept` no input é só um filtro do seletor do SO — não uma
    // garantia. `File.type` chega como `string` puro; sem esta guarda,
    // o TS não aceita passar para `tipo`, que o schema estreita ao
    // union de MIME aceitos.
    const tipo = arquivo.type;
    if (!ehTipoDeAnexo(tipo)) {
      avisar(
        'Lançamento salvo, mas o comprovante não é PDF, JPG, PNG ou WebP — não foi anexado.',
        'aviso',
      );
      return;
    }
    try {
      const pedido = await financeiroApi.pedirEnvioDeAnexo(lancamentoId, {
        nome: arquivo.name,
        tipo,
        tamanho: arquivo.size,
      });
      // Fetch CRU: SEM api(), que adicionaria Authorization e
      // content-type: application/json — os dois errados aqui.
      const resposta = await fetch(comBase(pedido.url), {
        method: pedido.metodo,
        headers: pedido.cabecalhos,
        body: arquivo,
      });
      if (!resposta.ok) throw new Error('Falha no envio do arquivo.');
      await financeiroApi.confirmarAnexo(lancamentoId, {
        anexoId: pedido.anexoId,
        nome: arquivo.name,
      });
    } catch {
      avisar(
        'Lançamento salvo, mas o comprovante não foi anexado. Tente anexar de novo depois.',
        'aviso',
      );
    }
  }

  async function salvar() {
    setErros({});
    setErroGeral(null);

    const centavos = paraCentavos(valor);
    if (centavos === null) {
      setErros({
        valor: valor.trim() ? 'Valor inválido — use 120 ou 120,50.' : 'Informe o valor.',
      });
      return;
    }

    const pedido = zNovoLancamento.safeParse({
      tipo,
      categoriaId: categoriaEfetiva,
      ...(subcategoria.trim() ? { subcategoria: subcategoria.trim() } : {}),
      valor: centavos,
      data,
      centroDeCusto,
      formaPagamento: forma,
      situacao,
      ...(recorrencia ? { recorrencia } : {}),
    });
    if (!pedido.success) {
      setErros(errosPorCampo(pedido.error.issues));
      return;
    }

    try {
      const criado = await criar.mutateAsync(pedido.data);
      await enviarAnexoSeHouver(criado.lancamento.id);
      avisar(
        criado.recorrencia
          ? 'Lançamento salvo e replicado nos próximos 12 meses.'
          : 'Lançamento salvo.',
      );
      onFechar();
    } catch (erro) {
      if (erro instanceof ErroDeApi && erro.campos) setErros(primeiraMensagem(erro.campos));
      setErroGeral(mensagemDoErro(erro));
    }
  }

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo="Novo lançamento"
      descricao="Entrada ou saída, com centro de custo e comprovante."
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => void salvar()} disabled={criar.isPending}>
            {criar.isPending ? 'Salvando…' : 'Salvar lançamento'}
          </Botao>
        </>
      }
    >
      <CorpoDeModal>
        <Campo rotulo="Tipo" erro={erros['tipo']}>
          <Escolhas
            aria-label="Tipo do lançamento"
            opcoes={TIPO_LANCAMENTO.map((t) => ({ valor: t, rotulo: ROTULO_TIPO_LANCAMENTO[t] }))}
            valor={tipo}
            onChange={setTipo}
          />
        </Campo>

        <Formulario colunas={2} onSubmit={(e) => e.preventDefault()}>
          <Campo
            rotulo="Categoria"
            erro={
              erros['categoriaId'] ??
              (categoriasQuery.isError ? 'Não consegui carregar as categorias.' : undefined)
            }
          >
            <select
              value={categoriaEfetiva}
              onChange={(e) => setCategoriaId(e.target.value)}
              disabled={categoriasQuery.isPending || categoriasDoTipo.length === 0}
            >
              {categoriasQuery.isPending && <option value="">Carregando…</option>}
              {!categoriasQuery.isPending && categoriasDoTipo.length === 0 && (
                <option value="">
                  Nenhuma categoria de {ROTULO_TIPO_LANCAMENTO[tipo].toLowerCase()}
                </option>
              )}
              {categoriasDoTipo.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Subcategoria" erro={erros['subcategoria']}>
            <input
              placeholder="Ex.: folha quinzenal"
              value={subcategoria}
              onChange={(e) => setSubcategoria(e.target.value)}
            />
          </Campo>
          <Campo rotulo="Valor" erro={erros['valor']}>
            <input
              inputMode="decimal"
              placeholder="R$ 0,00"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
            />
          </Campo>
          <Campo rotulo="Data" erro={erros['data']}>
            <input type="date" value={data} onChange={(e) => setData(e.target.value)} />
          </Campo>
          <Campo rotulo="Centro de custo" erro={erros['centroDeCusto']}>
            <select
              value={centroDeCusto}
              onChange={(e) => setCentroDeCusto(e.target.value as CentroDeCusto)}
            >
              {CENTRO_DE_CUSTO.map((c) => (
                <option key={c} value={c}>
                  {ROTULO_CENTRO_DE_CUSTO[c]}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Forma de pagamento" erro={erros['formaPagamento']}>
            <select value={forma} onChange={(e) => setForma(e.target.value as FormaPagamento)}>
              {FORMAS_DO_LANCAMENTO_MANUAL.map((f) => (
                <option key={f} value={f}>
                  {ROTULO_FORMA_PAGAMENTO[f]}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Situação" erro={erros['situacao']}>
            <select
              value={situacao}
              onChange={(e) => setSituacao(e.target.value as SituacaoLancamento)}
            >
              {SITUACAO_LANCAMENTO.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Recorrência" erro={erros['recorrencia']}>
            <select
              value={recorrencia}
              onChange={(e) => setRecorrencia(e.target.value as FrequenciaRecorrencia | '')}
            >
              <option value="">Não se repete</option>
              {FREQUENCIA_RECORRENCIA.map((f) => (
                <option key={f} value={f}>
                  {ROTULO_FREQUENCIA[f]}
                </option>
              ))}
            </select>
          </Campo>
        </Formulario>

        <Campo rotulo="Comprovante" ajuda="PDF, JPG, PNG ou WebP · até 10 MB. Opcional.">
          <label className="empty" style={{ display: 'block', cursor: 'pointer' }}>
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp"
              className="sr-only"
              onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
            />
            <Icone nome="clip" tamanho={22} />
            <br />
            {arquivo ? arquivo.name : 'Anexar comprovante'}
          </label>
        </Campo>

        {erroGeral && (
          <div className="aviso conflito" role="alert">
            <Icone nome="info" />
            <div>{erroGeral}</div>
          </div>
        )}
      </CorpoDeModal>
    </Modal>
  );
}

/**
 * O primeiro erro de cada campo, pelo caminho do zod ("recorrencia").
 * O mesmo padrão de `agenda/ModalDeReserva.tsx`, reimplementado aqui
 * (e não importado de lá) para não acoplar `financeiro` a um arquivo
 * de tela de outro módulo — as duas cópias são pequenas o bastante
 * para valer a independência.
 */
function errosPorCampo(
  issues: readonly { path: readonly (string | number)[]; message: string }[],
): Record<string, string> {
  const erros: Record<string, string> = {};
  for (const issue of issues) {
    const campo = issue.path.join('.') || '_';
    erros[campo] ??= issue.message;
  }
  return erros;
}

function primeiraMensagem(campos: Record<string, string[]>): Record<string, string> {
  const erros: Record<string, string> = {};
  for (const [campo, mensagens] of Object.entries(campos)) {
    if (mensagens[0]) erros[campo] = mensagens[0];
  }
  return erros;
}
